import React, { useState, useRef } from 'react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, ArrowDown } from 'lucide-react';
export function Hero() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const toggleAudio = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };
  return <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 bg-[#FAFAF8] overflow-hidden" data-id="element-167">
      {/* Decorative background element */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none opacity-30" data-id="element-168">
        <div className="absolute top-20 right-0 w-96 h-96 bg-[#D8F3DC] rounded-full blur-3xl mix-blend-multiply" data-id="element-169" />
        <div className="absolute top-40 left-0 w-72 h-72 bg-[#E9F5EB] rounded-full blur-3xl mix-blend-multiply" data-id="element-170" />
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-3xl relative z-10" data-id="element-171">
        {/* Audio message section */}
        <FadeIn data-id="element-172">
          <div className="mb-10 bg-[#1B4332] rounded-2xl p-6 md:p-8 text-center shadow-lg" data-id="element-173">
            <audio ref={audioRef} src="https://res.cloudinary.com/dyjeiw2k1/video/upload/v1770619608/Hay_un_tipo_de_cansa_a0eomr.mp3" preload="metadata" onEnded={handleAudioEnded} data-id="element-174" />

            <div className="flex items-center justify-center gap-2 mb-4" data-id="element-175">
              <Volume2 className="w-5 h-5 text-[#D8F3DC]" data-id="element-176" />
              <p className="text-[#D8F3DC] text-sm font-semibold uppercase tracking-wider" data-id="element-177">
                Escuchá este mensaje
              </p>
            </div>

            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={toggleAudio} className="mx-auto mb-5 w-16 h-16 rounded-full bg-white/10 border-2 border-[#D8F3DC] flex items-center justify-center hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-[#D8F3DC] focus:ring-offset-2 focus:ring-offset-[#1B4332]" aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'} data-id="element-178">
              {isPlaying ? <Pause className="w-7 h-7 text-white" data-id="element-179" /> : <Play className="w-7 h-7 text-white ml-1" data-id="element-180" />}
            </motion.button>

            {/* Animated bars when playing */}
            <div className="flex items-end justify-center gap-1 h-6 mb-5" data-id="element-181">
              {[0, 1, 2, 3, 4, 5, 6].map(i => <motion.div key={i} className="w-1 rounded-full bg-[#D8F3DC]" animate={isPlaying ? {
              height: [4, 12 + Math.random() * 12, 6, 16 + Math.random() * 8, 4]
            } : {
              height: 4
            }} transition={isPlaying ? {
              duration: 0.8 + Math.random() * 0.4,
              repeat: Infinity,
              repeatType: 'reverse',
              delay: i * 0.1
            } : {
              duration: 0.3
            }} data-id="element-182" />)}
            </div>

            <motion.button whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} onClick={() => document.getElementById('checkout')?.scrollIntoView({
            behavior: 'smooth'
          })} className="inline-flex items-center justify-center gap-2 bg-white text-[#1B4332] font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#D8F3DC] transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#1B4332]" data-id="element-183">
              Empezar hoy
              <ArrowDown className="w-5 h-5" data-id="element-184" />
            </motion.button>
          </div>
        </FadeIn>

        <FadeIn data-id="element-185">
          <div className="text-center mb-12" data-id="element-186">
            <span className="inline-block py-1 px-3 rounded-full bg-[#D8F3DC] text-[#1B4332] text-sm font-semibold mb-6 tracking-wide uppercase" data-id="element-187">
              Protocolo de Recuperación
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#111111] leading-tight mb-6 font-serif" data-id="element-188">
              Volvé a caminar sin castigo
            </h1>
            <p className="text-xl md:text-2xl text-[#1A1A1A] font-medium max-w-2xl mx-auto leading-relaxed" data-id="element-189">
              En 14 días, en casa, con un plan que tu rodilla sí tolera.
            </p>
          </div>
        </FadeIn>

        {/* Hero image — before/after walking */}
        <FadeIn delay={0.15} data-id="element-190">
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl shadow-[#1B4332]/10" data-id="element-191">
            <img src="https://cdn.magicpatterns.com/uploads/5ozfgQF2zDbxBpeS8vdyD2/heylloyd-image-1770617132500.png" alt="Antes y después: caminar sin castigo, más estabilidad" className="w-full h-auto object-cover" data-id="element-192" />
          </div>
        </FadeIn>

        <FadeIn delay={0.25} data-id="element-193">
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl shadow-[#1B4332]/5 border border-gray-100 mb-10" data-id="element-194">
            <div className="prose prose-lg prose-p:text-[#1A1A1A] prose-p:leading-relaxed max-w-none" data-id="element-195">
              <p className="font-serif text-xl italic text-[#1B4332] mb-6" data-id="element-196">
                Hay un tipo de cansancio que no se nota desde afuera.
              </p>
              <p className="mb-4" data-id="element-197">
                Es cuando te invitan a salir y vos ya estás calculando el
                precio: cuántas cuadras… dónde hay escalones… si hay que esperar
                parado… cómo vas a levantarte mañana.
              </p>
              <p className="mb-4 font-medium" data-id="element-198">
                No es solo dolor. Es desconfianza.
              </p>
              <p className="mb-6" data-id="element-199">
                Y cuando no confiás en tu rodilla, tu vida se empieza a achicar
                sin que nadie lo anuncie.
              </p>
              <div className="w-16 h-1 bg-[#D8F3DC] my-8 mx-auto rounded-full" data-id="element-200" />
              <p className="text-lg font-medium text-center" data-id="element-201">
                Rodilla Sin Miedo es un protocolo corto, en casa, diseñado para
                una sola cosa: que vuelvas a caminar sin castigo… y que
                subir/bajar escaleras deje de sentirse como una prueba.
              </p>
            </div>
          </div>
        </FadeIn>

        <FadeIn delay={0.4} className="text-center" data-id="element-202">
          <Button size="lg" withIcon scrollTo="checkout" data-id="element-203">
            Quiero empezar hoy
          </Button>
          <p className="mt-4 text-sm text-gray-500" data-id="element-204">
            Descarga inmediata • Acceso de por vida
          </p>
        </FadeIn>
      </div>
    </section>;
}